// Import getAuthToken from api.js
import { getAuthToken } from "./api";

// Base URL for the API
export const BASE_URL = "https://api.panchalidesignhub.com/api";

// Import or define the isAuthenticated function
const isAuthenticated = () => {
  const token = localStorage.getItem("token");
  return !!token;
};

// API endpoints for user UI
export const UI_ENDPOINTS = {
  LOGIN_IMAGES: "/media/login-images/",
  LOGO: "/media/logo/",
  CAROUSEL: "/media/carousel/active/",
  CATEGORIES: "/products/categories/",
  COLLECTIONS: "/products/tags/",
  YOUTUBE_VIDEO: "/media/youtube-video/",
  PRODUCTS: "/products/products/",
  PROFILE: "/accounts/profile/",
  ADDRESSES: "/accounts/addresses/",
  ADDRESS_DETAIL: (id) => `/accounts/addresses/${id}/`,
  ADDRESS_TOGGLE_ACTIVE: (id) => `/accounts/addresses/${id}/toggle_active/`,
  CART: "/orders/cart/",
  CART_ITEM: (id) => `/orders/cart/${id}/`,
  WISHLIST: "/orders/wishlist/",
  WISHLIST_DETAIL: (id) => `/orders/wishlist/${id}/`, // Changed from product/${id}
  NEW_ARRIVALS: "/products/products/new_arrivals/",
  INDEX_BANNER: "/media/index-banner/",
  BRANDS: "/products/brands/",
  PRODUCT_DETAIL: (id) => `/products/products/${id}/`,
  RELATED_PRODUCTS: (categoryId) =>
    `/products/products/?category=${categoryId}`,
  ABOUT_IMAGES: "/media/about-images/",
  ABOUT_CONTENT: "/media/about-content/",
  RETURN_ORDER: "/orders/returns/",
  SUBMIT_REVIEW: (productId) => `/products/products/${productId}/reviews/`, // Fixed endpoint format
  ADD_TO_CART: "/orders/cart/",
  CREATE_ORDER: "/orders/orders/",
  VERIFY_PAYMENT: (orderNumber) =>
    `/orders/orders/${orderNumber}/test_payment_success/`,
  PRODUCT_REVIEWS: "/products/reviews/",
  CONTACT_FORM: "/additional/contact/",
  NEWSLETTER: "/additional/newsletter/",
};

// Base API request function
export const apiRequest = async (endpoint, options = {}) => {
  const { method = "GET", body, headers = {}, skipAuth = false } = options;

  // Set up request headers
  const requestHeaders = {
    "Content-Type": "application/json",
    ...headers,
  };

  // Add auth token if not skipping authentication and token exists
  if (!skipAuth) {
    const token = localStorage.getItem("token");
    if (token) {
      requestHeaders["Authorization"] = `Token ${token}`;
    } else if (endpoint.includes("reviews") || endpoint.includes("cart")) {
      // If endpoint requires auth but no token exists, handle gracefully
      console.warn(
        "Auth required for endpoint but no token available:",
        endpoint
      );
      if (endpoint.includes("reviews")) {
        return []; // Return empty array for reviews
      }
      throw new Error("Please login to access this feature");
    }
  }

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method,
      headers: requestHeaders,
      body,
    });

    if (!response.ok) {
      // Try to get more detailed error information
      try {
        const errorData = await response.json();
        console.error("API error details:", errorData);

        // Handle 401 errors more gracefully
        if (response.status === 401) {
          if (endpoint.includes("reviews")) {
            return []; // Return empty array for reviews
          }
          throw new Error("Please login to access this feature");
        }

        throw new Error(
          `API request failed with status ${response.status}: ${JSON.stringify(
            errorData
          )}`
        );
      } catch (error) {
        console.error("API error response parsing failed:", error);
        throw new Error(`API request failed with status ${response.status}`);
      }
    }

    // For DELETE requests or when no content is expected
    if (response.status === 204 || method === "DELETE") {
      return true;
    }

    return await response.json();
  } catch (error) {
    console.error("API request error:", error);
    throw error;
  }
};

// User UI specific API methods
export const userApi = {
  // Get login images
  getLoginImages: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.LOGIN_IMAGES, {
        method: "GET",
        skipAuth: true, // Skip authentication for public resource
      });
      return Array.isArray(response) ? response : [response];
    } catch (error) {
      console.error("Get login images error:", error);
      throw error;
    }
  },

  // Get logo
  getLogo: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.LOGO, {
        method: "GET",
        skipAuth: true, // Skip authentication for public resource
      });
      return response;
    } catch (error) {
      console.error("Get logo error:", error);
      throw error;
    }
  },

  // Get active carousels
  getActiveCarousels: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.CAROUSEL, {
        method: "GET",
        skipAuth: true, // Skip authentication for public resource
      });
      return response;
    } catch (error) {
      console.error("Get active carousels error:", error);
      throw error;
    }
  },

  // Get categories
  getCategories: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.CATEGORIES, {
        method: "GET",
        skipAuth: true, // Skip authentication for public resource
      });
      return response;
    } catch (error) {
      console.error("Get categories error:", error);
      throw error;
    }
  },

  // Get collections (tags)
  getCollections: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.COLLECTIONS, {
        method: "GET",
        skipAuth: true, // Public endpoint
      });
      return Array.isArray(response) ? response : [response];
    } catch (error) {
      console.error("Get collections error:", error);
      throw error;
    }
  },

  // Get YouTube video
  getYoutubeVideo: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.YOUTUBE_VIDEO, {
        method: "GET",
        skipAuth: true, // Skip authentication for public resource
      });
      return response;
    } catch (error) {
      console.error("Get YouTube video error:", error);
      throw error;
    }
  },

  getLatestProducts: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.PRODUCTS, {
        method: "GET",
        skipAuth: true, // Public listing
      });
      return Array.isArray(response) ? response : [response];
    } catch (error) {
      console.error("Get products error:", error);
      throw error;
    }
  },

  // Get user profile
  getProfile: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.PROFILE, {
        method: "GET",
      });
      return response;
    } catch (error) {
      console.error("Get profile error:", error);
      throw error;
    }
  },

  // Update user profile
  updateProfile: async (profileData) => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.PROFILE, {
        method: "PATCH",
        body: JSON.stringify(profileData),
      });
      return response;
    } catch (error) {
      console.error("Update profile error:", error);
      throw error;
    }
  },

  // Get all addresses
  getAddresses: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.ADDRESSES, {
        method: "GET",
      });
      return Array.isArray(response) ? response : [response];
    } catch (error) {
      console.error("Get addresses error:", error);
      throw error;
    }
  },

  // Add new address
  addAddress: async (addressData) => {
    try {
      // Ensure all required fields are present and properly formatted
      const formattedAddress = {
        address_type: addressData.address_type.toLowerCase(),
        first_name: addressData.first_name,
        last_name: addressData.last_name,
        company_name: addressData.company_name || "",
        country: addressData.country,
        street_address_1: addressData.street_address_1,
        street_address_2: addressData.street_address_2 || "",
        city: addressData.city,
        state: addressData.state,
        pincode: addressData.pincode,
        phone: addressData.phone,
        email: addressData.email,
        order_notes: addressData.order_notes || "",
        is_default: addressData.is_default || false,
      };

      const response = await apiRequest(UI_ENDPOINTS.ADDRESSES, {
        method: "POST",
        body: JSON.stringify(formattedAddress),
      });
      return response;
    } catch (error) {
      console.error("Add address error:", error);
      throw error;
    }
  },

  // Update address
  updateAddress: async (id, addressData) => {
    try {
      // Ensure all required fields are present and properly formatted
      const formattedAddress = {
        address_type: addressData.address_type.toLowerCase(),
        first_name: addressData.first_name,
        last_name: addressData.last_name,
        company_name: addressData.company_name || "",
        country: addressData.country,
        street_address_1: addressData.street_address_1,
        street_address_2: addressData.street_address_2 || "",
        city: addressData.city,
        state: addressData.state,
        pincode: addressData.pincode,
        phone: addressData.phone,
        email: addressData.email,
        order_notes: addressData.order_notes || "",
        is_default: addressData.is_default || false,
      };

      const response = await apiRequest(UI_ENDPOINTS.ADDRESS_DETAIL(id), {
        method: "PATCH",
        body: JSON.stringify(formattedAddress),
      });
      return response;
    } catch (error) {
      console.error("Update address error:", error);
      throw error;
    }
  },

  // Delete address
  deleteAddress: async (addressId) => {
    try {
      await apiRequest(`/accounts/addresses/${addressId}/`, {
        method: "DELETE",
      });
      return true;
    } catch (error) {
      console.error("Delete address error:", error);
      throw error;
    }
  },

  // Toggle address active status
  toggleAddressActive: async (addressId) => {
    try {
      const response = await apiRequest(
        UI_ENDPOINTS.ADDRESS_TOGGLE_ACTIVE(addressId),
        {
          method: "PATCH",
        }
      );
      return response;
    } catch (error) {
      console.error("Toggle address status error:", error);
      throw error;
    }
  },

  // Get cart items
  getCartItems: async () => {
    if (!isAuthenticated()) {
      throw new Error("Please login to view cart");
    }

    const response = await apiRequest(UI_ENDPOINTS.CART, {
      method: "GET",
    });

    const transformedCart = response.map((item) => {
      const basePrice = parseFloat(item.product_details?.price || 0);
      const salePrice = parseFloat(
        item.product_details?.sale_price || basePrice
      );
      // Get final_price from the top level of the item, not from product_details
      const finalPrice = parseFloat(item.final_price || salePrice);

      console.log("Extracted prices:", { basePrice, salePrice, finalPrice });

      const quantity = parseInt(item.quantity || 1);

      return {
        id: item.product_details?.id,
        cartItemId: item.id,
        name: item.product_details?.name || "Untitled Product",
        originalPrice: basePrice,
        salePrice: salePrice,
        finalPrice: finalPrice,
        image:
          item.variant?.images?.[0]?.images ||
          "/images/placeholder-product.png",
        size: item.variant?.size || "Standard",
        color: item.variant?.color || "Default",
        quantity: quantity,
        stock: item.variant?.stock || 0,
        variant_id: item.variant?.id,
        itemSubtotal: basePrice * quantity,
        itemDiscount: (basePrice - finalPrice) * quantity,
        itemTotal: finalPrice * quantity,
      };
    });

    return transformedCart;
  },

  // Update cart item quantity
  updateCartItemQuantity: async (cartItemId, quantity) => {
    if (!isAuthenticated()) {
      throw new Error("Please login to update cart");
    }

    const response = await apiRequest(`${UI_ENDPOINTS.CART}${cartItemId}/`, {
      method: "PATCH",
      body: JSON.stringify({ quantity }),
    });

    if (!response) {
      throw new Error("Failed to update cart quantity");
    }

    return response;
  },

  // Remove item from cart
  removeFromCart: async (cartItemId) => {
    try {
      if (!isAuthenticated()) {
        throw new Error("Please login to remove items from cart");
      }

      await apiRequest(UI_ENDPOINTS.CART_ITEM(cartItemId), {
        method: "DELETE",
      });
      return true; // Return true to indicate success
    } catch (error) {
      console.error("Remove from cart error:", error);
      throw error;
    }
  },

  getWishlist: async () => {
    try {
      if (!isAuthenticated()) {
        return [];
      }

      const response = await apiRequest(UI_ENDPOINTS.WISHLIST, {
        method: "GET",
      });

      return response || [];
    } catch (error) {
      console.error("Get wishlist error:", error);
      return [];
    }
  },

  removeFromWishlist: async (productId) => {
    try {
      if (!isAuthenticated()) {
        throw new Error("Please login to remove from wishlist");
      }

      const wishlist = await userApi.getWishlist();
      const wishlistItem = wishlist.find(
        (item) => item.product?.id === productId
      );

      if (!wishlistItem) {
        return { message: "Item not found in wishlist" };
      }

      const response = await fetch(
        `${BASE_URL}${UI_ENDPOINTS.WISHLIST_DETAIL(wishlistItem.id)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Token ${getAuthToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to remove item: ${response.status}`);
      }

      // Don't try to parse JSON for DELETE request
      return { message: "Item removed from wishlist" };
    } catch (error) {
      console.error("Remove from wishlist error:", error);
      throw error;
    }
  },

  addToWishlist: async (productId) => {
    try {
      if (!isAuthenticated()) {
        throw new Error("Please login to add to wishlist");
      }

      // First check if item already exists in wishlist
      const wishlist = await userApi.getWishlist();
      const exists = wishlist.some((item) => item.product?.id === productId);

      if (exists) {
        return { message: "Item already in wishlist" };
      }

      const response = await apiRequest(UI_ENDPOINTS.WISHLIST, {
        method: "POST",
        body: JSON.stringify({ product_id: productId }),
      });

      return response;
    } catch (error) {
      console.error("Add to wishlist error:", error);
      throw error;
    }
  },

  getNewArrivals: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.NEW_ARRIVALS, {
        method: "GET",
        skipAuth: true, // Skip authentication for public resource
      });
      return response;
    } catch (error) {
      console.error("Get new arrivals error:", error);
      throw error;
    }
  },

  getIndexBanner: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.INDEX_BANNER, {
        method: "GET",
        skipAuth: true, // Skip authentication for public resource
      });
      return response;
    } catch (error) {
      console.error("Get index banner error:", error);
      throw error;
    }
  },

  getBrands: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.BRANDS, {
        method: "GET",
        skipAuth: true, // Public brands list
      });
      return Array.isArray(response) ? response : [response];
    } catch (error) {
      console.error("Get brands error:", error);
      throw error;
    }
  },

  getProductDetail: async (productId) => {
    try {
      const response = await apiRequest(
        UI_ENDPOINTS.PRODUCT_DETAIL(productId),
        {
          method: "GET",
          skipAuth: true, // Product detail is public
        }
      );
      return response;
    } catch (error) {
      console.error("Get product detail error:", error);
      throw error;
    }
  },

  getRelatedProducts: async (categoryId) => {
    try {
      const response = await apiRequest(
        UI_ENDPOINTS.RELATED_PRODUCTS(categoryId),
        {
          method: "GET",
          skipAuth: true, // Public related products
        }
      );
      return Array.isArray(response) ? response : [response];
    } catch (error) {
      console.error("Get related products error:", error);
      throw error;
    }
  },

  // Add to cart
  addItemToCart: async (cartData) => {
    try {
      if (!isAuthenticated()) {
        throw new Error("Please login to add items to cart");
      }

      // Ensure we're sending the correct data format
      const payload = {
        product_id: cartData.product_id,
        variant_id: cartData.variant_id,
        quantity: cartData.quantity,
      };

      const response = await apiRequest(UI_ENDPOINTS.CART, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      return response;
    } catch (error) {
      console.error("Add to cart error:", error);
      throw error;
    }
  },

  // Get about page images
  getAboutImages: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.ABOUT_IMAGES, {
        method: "GET",
        skipAuth: true, // Public
      });
      return response;
    } catch (error) {
      console.error("Get about images error:", error);
      throw error;
    }
  },

  getAboutContent: async () => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.ABOUT_CONTENT, {
        method: "GET",
        skipAuth: true, // Public
      });
      return Array.isArray(response) ? response : [response];
    } catch (error) {
      console.error("Get about content error:", error);
      throw error;
    }
  },

  submitReturnRequest: async (returnData) => {
    try {
      console.log("API - Return request data received:", returnData);

      // Validate required fields
      if (!returnData?.order_number) {
        throw new Error("Order number is required");
      }
      if (!returnData?.reason) {
        throw new Error("Return reason is required");
      }
      if (!returnData?.description) {
        throw new Error("Return description is required");
      }

      const formattedData = {
        order_number: returnData.order_number,
        reason: returnData.reason.trim(),
        description: returnData.description.trim(),
      };

      console.log("API - Sending formatted return request:", formattedData);

      const response = await apiRequest(UI_ENDPOINTS.RETURN_ORDER, {
        method: "POST",
        body: JSON.stringify(formattedData),
      });

      return response;
    } catch (error) {
      console.error("Submit return request error:", error);
      if (error.response?.data?.order_number) {
        throw new Error(error.response.data.order_number[0]);
      }
      throw error;
    }
  },

  submitReview: async (reviewData) => {
    try {
      if (!reviewData.productId) {
        throw new Error("Product ID is required");
      }

      // Debug log
      console.log("Submitting review with data:", reviewData);

      const response = await apiRequest(
        UI_ENDPOINTS.SUBMIT_REVIEW(reviewData.productId),
        {
          method: "POST",
          body: JSON.stringify({
            product: reviewData.productId, // Using 'product' as the field name expected by the API
            rating: reviewData.rating,
            description: reviewData.review,
          }),
        }
      );
      return response;
    } catch (error) {
      console.error("Submit review error:", error);
      throw error;
    }
  },

  addToCart: async (productData) => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.ADD_TO_CART, {
        method: "POST",
        body: JSON.stringify(productData),
      });
      return response;
    } catch (error) {
      console.error("Add to cart error:", error);
      throw error;
    }
  },

  createOrder: async (orderData) => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.CREATE_ORDER, {
        method: "POST",
        body: JSON.stringify({
          shipping_address_id: orderData.shipping_address_id,
          billing_address_id: orderData.billing_address_id,
        }),
      });
      return response;
    } catch (error) {
      console.error("Create order error:", error);
      throw error;
    }
  },

  verifyPayment: async (orderNumber, paymentData) => {
    try {
      const response = await apiRequest(
        UI_ENDPOINTS.VERIFY_PAYMENT(orderNumber),
        {
          method: "POST",
          body: JSON.stringify({
            razorpay_order_id: paymentData.razorpay_order_id,
            razorpay_payment_id: paymentData.razorpay_payment_id,
            razorpay_signature: paymentData.razorpay_signature,
          }),
        }
      );
      return response;
    } catch (error) {
      console.error("Payment verification error:", error);
      throw error;
    }
  },

  getProductReviews: async (productId = null) => {
    try {
      // First try with authentication if available
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const authResponse = await fetch(`${BASE_URL}/products/reviews/`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${token}`,
            },
          });

          if (authResponse.ok) {
            return await authResponse.json();
          }
        } catch (authError) {
          console.log("Authenticated reviews fetch failed, trying alternative");
          console.error(authError);
        }
      }

      // If authenticated request fails or no token, try product-specific reviews
      if (productId) {
        try {
          const productResponse = await fetch(
            `${BASE_URL}/products/products/${productId}/reviews/`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          if (productResponse.ok) {
            return await productResponse.json();
          }
        } catch (productError) {
          console.log("Product-specific reviews fetch failed");
          console.error(productError);
        }
      }

      // If all attempts fail, return empty array without error
      return [];
    } catch (error) {
      console.error("Get product reviews error:", error);
      return [];
    }
  },

  getJobs: async () => {
    try {
      const response = await apiRequest("/careers/jobs/", {
        method: "GET",
      });
      return Array.isArray(response) ? response : [response];
    } catch (error) {
      console.error("Get jobs error:", error);
      throw error;
    }
  },

  submitContactForm: async (contactData) => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.CONTACT_FORM, {
        method: "POST",
        body: JSON.stringify({
          name: contactData.name,
          email: contactData.email,
          phone_number: contactData.phone,
          subject: contactData.subject,
          message: contactData.message,
        }),
        skipAuth: true, // Contact form doesn't require authentication
      });
      return response;
    } catch (error) {
      console.error("Contact form submission error:", error);
      throw error;
    }
  },

  subscribeNewsletter: async (email) => {
    try {
      const response = await apiRequest(UI_ENDPOINTS.NEWSLETTER, {
        method: "POST",
        body: JSON.stringify({ email }),
        skipAuth: true, // Newsletter subscription doesn't require authentication
      });
      return response;
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      throw error;
    }
  },
};

export const getProducts = async () => {
  const response = await fetch(`${BASE_URL}/products/products/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch products");
  }

  return response.json();
};

export const getUserOrders = async () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    console.log("Fetching orders from:", `${BASE_URL}/orders/orders/`); // Debug log
    console.log("Using token:", token); // Debug log (remove in production)

    const response = await fetch(`${BASE_URL}/orders/orders/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
    });

    console.log("Response status:", response.status); // Debug log

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        detail: `HTTP error! status: ${response.status}`,
      }));
      console.error("Error response:", errorData); // Debug log
      throw new Error(
        errorData.detail || `Failed to fetch orders (${response.status})`
      );
    }

    const data = await response.json();
    console.log("Orders data:", data); // Debug log
    return data;
  } catch (error) {
    console.error("Error in getUserOrders:", error); // Debug log
    throw error;
  }
};

export default userApi;
